import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';

import { CustomSearchComponent } from '@connect-services/client/components/custom-search/custom-search.component';
import { Layer } from '@connect-services/client/sdk/api';
import { DataExportService } from '@connect-services/client/services/data-export.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

export interface LayerRow {
    checkboxState: CheckboxState;
    isChecked: boolean;
    layerIds: string[];
    layerName: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CheckboxComponent, CustomSearchComponent, ExpansionPanelComponent, LoadingComponent, RessourcesModule],
    selector: 'app-geographic-zone',
    styleUrl: './geographic-zone.component.scss',
    templateUrl: './geographic-zone.component.html',
})
export class GeographicZoneComponent implements OnInit {
    protected readonly isLoading = signal<boolean>(false);
    protected readonly layerCheckboxState = computed<Record<string, CheckboxState>>(() =>
        this.getLayerCheckboxState(this.layerIdsByLayerName(), this.dataExportService.layerIds()),
    );
    protected readonly layerIdsByLayerName = signal<Record<string, string[]>>({});
    protected readonly layerNames = computed<string[]>((): string[] => Object.keys(this.layerIdsByLayerName()));
    protected readonly layerRows = computed<LayerRow[]>(() => {
        const checkboxStateByLayerName: Record<string, CheckboxState> = this.layerCheckboxState();

        return Object.entries(this.layerIdsByLayerName()).map(([layerName, layerIds]: [string, string[]]) => {
            const checkboxState: CheckboxState = checkboxStateByLayerName[layerName];

            return {
                checkboxState,
                isChecked: checkboxState === CheckboxState.CHECKED,
                layerIds,
                layerName,
            };
        });
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly CheckboxState = CheckboxState;
    protected readonly dataExportService = inject(DataExportService);

    private allLayersProject: Layer[] = [];
    private readonly searchValue = signal<string>('');

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly layerService = inject(LayerService);
    private readonly selectionStateService = inject(SelectionStateService);

    public async ngOnInit(): Promise<void> {
        this.isLoading.set(true);

        await this.loadAndGroupLayers();

        this.dataExportService.layerIds.set(new Set(this.getValidPreselectedLayerIds()));

        this.isLoading.set(false);
    }

    protected async handleSearch(searchValue: string): Promise<void> {
        this.searchValue.set(searchValue);
        this.isLoading.set(true);

        await this.loadAndGroupLayers(searchValue);

        this.isLoading.set(false);
    }

    protected updateSelectedLayer(layerIds: string[], currentState: CheckboxState): void {
        if (currentState === CheckboxState.CHECKED) {
            layerIds.forEach((layerId: string) => this.dataExportService.removeLayerId(layerId));

            return;
        }

        layerIds.forEach((layerId: string) => this.dataExportService.addLayerId(layerId));
    }

    private getGroupCheckboxState(layerIds: string[], checkedLayerIds: Set<string>): CheckboxState {
        const isAnyChecked: boolean = layerIds.some((layerId: string) => checkedLayerIds.has(layerId));

        return isAnyChecked ? CheckboxState.CHECKED : CheckboxState.UNCHECKED;
    }

    private getLayerCheckboxState(
        layerIdsByLayerName: Record<string, string[]>,
        checkedLayerIds: Set<string>,
    ): Record<string, CheckboxState> {
        return Object.fromEntries(
            Object.entries(layerIdsByLayerName).map(([layerName, groupedLayerIds]: [string, string[]]) => [
                layerName,
                this.getGroupCheckboxState(groupedLayerIds, checkedLayerIds),
            ]),
        );
    }

    private getValidPreselectedLayerIds(): string[] {
        const selectedLayerIds: string[] = Object.keys(this.selectionStateService.selectedEntitiesByLayerId());

        if (!selectedLayerIds.length) {
            return [];
        }

        const existingIds = new Set<string>(Object.values(this.layerIdsByLayerName()).flat());

        return selectedLayerIds.filter((layerId: string) => existingIds.has(layerId));
    }

    private async loadAndGroupLayers(searchValue?: string): Promise<void> {
        this.allLayersProject = (await this.layerService.getAllLayers({ hasGeom: true, searchValue })).items;

        this.layerIdsByLayerName.set(this.layerService.getGroupedLayers(this.allLayersProject));
    }
}
